import { CASE_MEMBER_TYPE, QUERY_KEYS } from "@/libraries/constant";
import { getIsoDateTime } from "@/libraries/date";
import { unix } from "@/libraries/dayjs-utc";
import { randInt } from "@/libraries/helper";
import { applyDataChanges } from "@/libraries/transform";
import type {
	AddRemoveMemberCasePayloadSchema,
	CreateCaseInfoPayloadSchema,
	CreateCasePayloadSchema,
	GetCaseSyncParamsSchema,
	PatientVisitFormSchema,
	PostAddLocationPayloadSchema,
	UpdateCasePayloadSchema,
} from "@/schemas";
import { caseService, type memberService } from "@/services";
import { encounterService } from "@/services/encounter";
import { useSyncStore } from "@/stores/sync";
import type {
	AssignPatientIntoCasePayloadSchema,
	AssignUnassignTeamPayload,
	Case,
	CaseInfo,
	CaseMedicalNotes,
	CasePatientVisit,
	GetCaseInfosResponse,
	GetCaseLettersResponse,
	GetCaseMedicalNotesResponse,
	GetCasePatientVisitsResponse,
	GetCaseResponse,
	GetCasesResponse,
	MemberProfileResponse,
	Patient,
	PostRequestAccessCasePayload,
} from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
	infiniteDataAdd,
	infiniteDataRemove,
	infiniteDataReplace,
} from "../utils/mutation";

export const caseMutations = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_SYNC], {
		retry: 2,
		mutationFn: (params: GetCaseSyncParamsSchema) => caseService.sync(params),
		onSuccess: (res) => {
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: applyDataChanges(p.cases || [], res.update, res.delete),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			useSyncStore.setState({
				caseSyncLastUnix: unix(),
			});
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_CREATE], {
		retry: 2,
		mutationFn: (payload: CreateCasePayloadSchema) =>
			caseService.create(payload),
		onMutate: async (newCase) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES],
			});
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			const createdAt = getIsoDateTime();
			const optimisticCase = {
				...newCase,
				id: randInt(),
				created_at: createdAt,
				updated_at: createdAt,
				user: currentUser?.user as unknown as Case["user"],
				last_message: {},
			} as Case;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[
					QUERY_KEYS.GET_CASES,
					{
						status: "active",
					},
				],
				(oldData) => infiniteDataAdd(oldData, "cases", optimisticCase),
			);
			return { optimisticCase };
		},
		onSuccess: (res, _variables, ctx) => {
			if (!ctx?.optimisticCase) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[
					QUERY_KEYS.GET_CASES,
					{
						status: "active",
					},
				],
				(oldData) =>
					infiniteDataReplace(
						oldData,
						"cases",
						"id",
						ctx.optimisticCase.id,
						res.case,
					),
			);
		},
		onError: (_err, _variables, ctx) => {
			if (!ctx?.optimisticCase) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[
					QUERY_KEYS.GET_CASES,
					{
						status: "active",
					},
				],
				(oldData) =>
					infiniteDataRemove(oldData, "cases", "id", ctx.optimisticCase.id),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_UPDATE], {
		retry: 2,
		mutationFn: (payload: UpdateCasePayloadSchema) =>
			caseService.update(payload),
		onMutate: async (updatedCase) => {
			const { id, ...newCase } = updatedCase;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE, id],
			});
			const previousCase = queryClient.getQueryData<GetCaseResponse>([
				QUERY_KEYS.GET_CASE,
				id,
			]);
			const optimisticCase = {
				...(previousCase ?? {}),
				...(newCase.title ? { title: newCase.title } : {}),
				...(newCase.discharged_date
					? {
							discharged_date: dayjs(
								newCase.discharged_date,
								"DD/MM/YYYY",
							).format("YYYY-MM-DD"),
						}
					: {}),
				...(newCase.discharged_time
					? { discharged_time: newCase.discharged_time }
					: {}),
				...(newCase.case_summary_attributes?.text
					? {
							case_summary: {
								description: newCase.case_summary_attributes.text,
							},
						}
					: {}),
				...(newCase.case_diagnose_attributes?.text
					? {
							case_diagnose: {
								description: newCase.case_diagnose_attributes.text,
							},
						}
					: {}),
				...(newCase.case_medication_attributes?.text
					? {
							case_medication: {
								description: newCase.case_medication_attributes.text,
							},
						}
					: {}),
				...(newCase.case_plan_attributes?.text
					? {
							case_plan: {
								description: newCase.case_plan_attributes.text,
							},
						}
					: {}),
				...(newCase.case_allergy_attributes?.text
					? {
							case_allergy: {
								description: newCase.case_allergy_attributes.text,
							},
						}
					: {}),
			} as Case;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) =>
								c.id === id ? { ...c, ...optimisticCase } : c,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, id],
				(oldData) =>
					!oldData
						? undefined
						: {
								...oldData,
								...optimisticCase,
							},
			);
			return { updatedCase, previousCase };
		},
		onSuccess: (res) => {
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) =>
								c.id === res.case.id ? { ...c, ...res.case } : c,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, res.case.id],
				(oldData) => (!oldData ? undefined : { ...oldData, ...res.case }),
			);
		},
		onError: (_err, variables, context) => {
			if (!context?.updatedCase) return;
			if (!context?.previousCase) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) =>
								c.id === variables.id ? (context.previousCase as Case) : c,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variables.id],
				() => context.previousCase,
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CHECK_PATIENT_IN_CASE], {
		retry: 1,
		mutationFn: (payload: Patient["ic_number"]) =>
			caseService.findByPatientId(payload),
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_ASSIGN_PATIENT], {
		retry: 2,
		mutationFn: (payload: AssignPatientIntoCasePayloadSchema) =>
			caseService.assignPatient(payload),
	});
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_CASE_ADD_PATIENT_LOCATION],
		{
			retry: 2,
			mutationFn: (payload: PostAddLocationPayloadSchema) =>
				caseService.addLocation(payload),
			onMutate: async (payload) => {
				await queryClient.cancelQueries({
					queryKey: [QUERY_KEYS.GET_CASES],
				});
				await queryClient.cancelQueries({
					queryKey: [QUERY_KEYS.GET_CASE, payload.caseId],
				});
				const previousCase = queryClient.getQueryData<GetCaseResponse>([
					QUERY_KEYS.GET_CASE,
					payload.caseId,
				]);
				queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
					[QUERY_KEYS.GET_CASES],
					(oldData) => ({
						...oldData,
						pages:
							oldData?.pages?.map((p) => ({
								...p,
								cases: p.cases.map((c) =>
									c.id === payload.caseId
										? {
												...c,
												locations: {
													...c.locations,
													id: c.locations.id ?? randInt(),
													current_location: payload.name,
													previously: c.locations.current_location
														? [
																c.locations.current_location,
																...c.locations.previously,
															]
														: [],
												},
											}
										: c,
								),
							})) || [],
						pageParams: oldData?.pageParams || [1],
					}),
				);
				queryClient.setQueryData<GetCaseResponse>(
					[QUERY_KEYS.GET_CASE, payload.caseId],
					(oldData) =>
						!oldData
							? undefined
							: {
									...oldData,
									locations: {
										...oldData.locations,
										current_location: payload.name,
									},
								},
				);

				return { previousCase };
			},
			onSuccess: (res, variables) => {
				queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
					[QUERY_KEYS.GET_CASES],
					(oldData) => ({
						...oldData,
						pages:
							oldData?.pages?.map((p) => ({
								...p,
								cases: p.cases.map((c) =>
									c.id === variables.caseId
										? {
												...c,
												locations: res.locations,
											}
										: c,
								),
							})) || [],
						pageParams: oldData?.pageParams || [1],
					}),
				);
				queryClient.setQueryData<GetCaseResponse>(
					[QUERY_KEYS.GET_CASE, variables.caseId],
					(oldData) =>
						!oldData
							? undefined
							: {
									...oldData,
									locations: res.locations,
								},
				);
			},
			onError: (_err, variables, context) => {
				if (!context?.previousCase) return;
				queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
					[QUERY_KEYS.GET_CASES],
					(oldData) => ({
						...oldData,
						pages:
							oldData?.pages?.map((p) => ({
								...p,
								cases: p.cases.map((c) =>
									c.id === variables.caseId
										? (context.previousCase as Case)
										: c,
								),
							})) || [],
						pageParams: oldData?.pageParams || [1],
					}),
				);
				queryClient.setQueryData<GetCaseResponse>(
					[QUERY_KEYS.GET_CASE, variables.caseId],
					(oldData) => (!oldData ? undefined : context.previousCase),
				);
			},
		},
	);
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_PATIENT_VISIT_CREATE], {
		retry: 2,
		mutationFn: (payload: PatientVisitFormSchema) =>
			caseService.addPatientVisit(payload),
		onMutate: async ({ caseId, ...newPatientVisit }) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE_PATIENT_VISITS, caseId],
			});
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			const createdAt = dayjs().toISOString();
			const optimisticPatientVisit = {
				...newPatientVisit,
				id: randInt(),
				user: currentUser?.user as unknown as CasePatientVisit["user"],
				created_at: createdAt,
				updated_at: createdAt,
			} as CasePatientVisit;
			queryClient.setQueryData<InfiniteData<GetCasePatientVisitsResponse>>(
				[QUERY_KEYS.GET_CASE_PATIENT_VISITS, caseId],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							patient_visits: applyDataChanges(
								p.patient_visits,
								[optimisticPatientVisit],
								[],
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			return { optimisticPatientVisit };
		},
		onSuccess: (res, variables, ctx) => {
			if (!ctx?.optimisticPatientVisit) return;
			queryClient.setQueryData<InfiniteData<GetCasePatientVisitsResponse>>(
				[QUERY_KEYS.GET_CASE_PATIENT_VISITS, variables.caseId],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							patient_visits: p.patient_visits.map((pv) =>
								pv.id === ctx.optimisticPatientVisit.id
									? res.patient_visit
									: pv,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
		},
		onError: (_err, variables, ctx) => {
			if (!ctx?.optimisticPatientVisit) return;
			queryClient.setQueryData<InfiniteData<GetCasePatientVisitsResponse>>(
				[QUERY_KEYS.GET_CASE_PATIENT_VISITS, variables.caseId],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							patient_visits: p.patient_visits.filter(
								(pv) => pv.id !== ctx.optimisticPatientVisit.id,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_INFO_CREATE], {
		retry: 2,
		mutationFn: (payload: CreateCaseInfoPayloadSchema) =>
			caseService.addCaseInfo(payload),
		onMutate: async ({ caseId, ...newCaseInfo }) => {
			const latestCaseInfoKey = [
				QUERY_KEYS.GET_CASE_INFOS,
				{ caseId, info_type: newCaseInfo.info_type, latest: true },
			];
			const listCaseInfoKey = [
				QUERY_KEYS.GET_CASE_INFOS,
				{ caseId, info_type: newCaseInfo.info_type, latest: false },
			];
			await queryClient.cancelQueries({
				queryKey: latestCaseInfoKey,
			});
			await queryClient.cancelQueries({
				queryKey: listCaseInfoKey,
			});
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			const createdAt = dayjs().toISOString();
			const optimisticCaseInfo = {
				id: randInt(),
				description: newCaseInfo.description,
				user: currentUser?.user as unknown as CasePatientVisit["user"],
				created_at: createdAt,
				updated_at: createdAt,
			} as CaseInfo;

			queryClient.setQueryData<GetCaseInfosResponse<true>>(
				latestCaseInfoKey,
				(oldData) =>
					!oldData
						? undefined
						: {
								...oldData,
								case_infos: optimisticCaseInfo,
							},
			);
			queryClient.setQueryData<InfiniteData<GetCaseInfosResponse<false>>>(
				listCaseInfoKey,
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							case_infos: applyDataChanges(
								p.case_infos || [],
								[optimisticCaseInfo],
								[],
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			return { optimisticCaseInfo };
		},
		onSuccess: (res, variables, ctx) => {
			if (!ctx?.optimisticCaseInfo) return;
			const latestCaseInfoKey = [
				QUERY_KEYS.GET_CASE_INFOS,
				{
					caseId: variables.caseId,
					info_type: variables.info_type,
					latest: true,
				},
			];
			const listCaseInfoKey = [
				QUERY_KEYS.GET_CASE_INFOS,
				{
					caseId: variables.caseId,
					info_type: variables.info_type,
					latest: false,
				},
			];
			queryClient.setQueryData<GetCaseInfosResponse<true>>(
				latestCaseInfoKey,
				(oldData) =>
					!oldData
						? undefined
						: {
								...oldData,
								case_infos: res.case_info,
							},
			);
			queryClient.setQueryData<InfiniteData<GetCaseInfosResponse<false>>>(
				listCaseInfoKey,
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							case_infos: p.case_infos.map((ci) =>
								ci.id === ctx.optimisticCaseInfo.id ? res.case_info : ci,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
		},
		onError: (_err, variables, ctx) => {
			if (!ctx?.optimisticCaseInfo) return;
			const latestCaseInfoKey = [
				QUERY_KEYS.GET_CASE_INFOS,
				{
					caseId: variables.caseId,
					info_type: variables.info_type,
					latest: true,
				},
			];
			const listCaseInfoKey = [
				QUERY_KEYS.GET_CASE_INFOS,
				{
					caseId: variables.caseId,
					info_type: variables.info_type,
					latest: false,
				},
			];
			queryClient.setQueryData<GetCaseInfosResponse<true>>(
				latestCaseInfoKey,
				(oldData) =>
					!oldData
						? undefined
						: {
								...oldData,
								case_infos: oldData.case_infos,
							},
			);
			queryClient.setQueryData<InfiniteData<GetCaseInfosResponse<false>>>(
				listCaseInfoKey,
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							case_infos: p.case_infos.filter(
								(ci) => ci.id !== ctx.optimisticCaseInfo.id,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_CLOSED], {
		retry: 2,
		mutationFn: (caseId: Case["id"]) => caseService.closedCases(caseId),
		onMutate: async (caseId) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES, { status: "active" }],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES, { status: "closed" }],
			});
			const previousCase = queryClient.getQueryData<GetCaseResponse>([
				QUERY_KEYS.GET_CASE,
				caseId,
			]);
			const optimisticCase = {
				...(previousCase || {}),
				status: "closed",
				closed_at: getIsoDateTime(),
			} as Case;
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, caseId],
				(oldData) => (!oldData ? oldData : optimisticCase),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => infiniteDataRemove(oldData, "cases", "id", caseId),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "closed" }],
				(oldData) => infiniteDataAdd(oldData, "cases", optimisticCase, true),
			);
			return { previousCase, optimisticCase };
		},
		onSuccess: () => {
			queryClient.setQueryData<Awaited<ReturnType<typeof memberService.home>>>(
				[QUERY_KEYS.GET_MEMBER_HOME],
				(oldData) =>
					!oldData
						? oldData
						: {
								...oldData,
								active_cases: oldData.active_cases - 1,
							},
			);
		},
		onError: (_err, variables, context) => {
			if (!context) return;
			const previousCase = context.previousCase as Case;
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variables],
				() => context.previousCase,
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => infiniteDataAdd(oldData, "cases", previousCase, true),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "closed" }],
				(oldData) => infiniteDataRemove(oldData, "cases", "id", variables),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_REOPEN], {
		retry: 2,
		mutationFn: (caseId: Case["id"]) => caseService.reopenCase(caseId),
		onMutate: async (caseId) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES, { status: "active" }],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES, { status: "closed" }],
			});
			const previousCase = queryClient.getQueryData<GetCaseResponse>([
				QUERY_KEYS.GET_CASE,
				caseId,
			]);
			const optimisticCase = {
				...(previousCase || {}),
				status: "active",
				closed_at: null,
			} as Case;
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, caseId],
				(oldData) => (!oldData ? oldData : optimisticCase),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => infiniteDataAdd(oldData, "cases", optimisticCase, true),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "closed" }],
				(oldData) => infiniteDataRemove(oldData, "cases", "id", caseId),
			);
			return { previousCase, optimisticCase };
		},
		onSuccess: () => {
			queryClient.setQueryData<Awaited<ReturnType<typeof memberService.home>>>(
				[QUERY_KEYS.GET_MEMBER_HOME],
				(oldData) =>
					!oldData
						? oldData
						: {
								...oldData,
								active_cases: oldData.active_cases + 1,
							},
			);
		},
		onError: (_err, variables, context) => {
			if (!context) return;
			const previousCase = context.previousCase as Case;
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variables],
				context.previousCase,
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => infiniteDataRemove(oldData, "cases", "id", variables),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "closed" }],
				(oldData) => infiniteDataAdd(oldData, "cases", previousCase, true),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_LEAVE], {
		retry: 2,
		mutationFn: (caseId: Case["id"]) => caseService.exitCase(caseId),
		onMutate: async (caseId) => {
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			if (!currentUser) return;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
			const previousCase = queryClient.getQueryData<GetCaseResponse>([
				QUERY_KEYS.GET_CASE,
				caseId,
			]);
			const optimisticCase = {
				...(previousCase || {}),
				internal_members:
					previousCase?.internal_members?.filter(
						(m) => m.id !== currentUser.user.id,
					) || [],
				external_members:
					previousCase?.external_members?.filter(
						(m) => m.id !== currentUser.user.id,
					) || [],
				members:
					previousCase?.members?.filter((m) => m.id !== currentUser.user.id) ||
					[],
				member_type: CASE_MEMBER_TYPE.NOT_MEMBER,
				status: "closed",
			} as Case;
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, caseId],
				(oldData) => (!oldData ? oldData : optimisticCase),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "closed" }],
				(oldData) => infiniteDataAdd(oldData, "cases", optimisticCase, true),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => infiniteDataRemove(oldData, "cases", "id", caseId),
			);
			return { previousCase, optimisticCase };
		},
		onSuccess: () => {
			queryClient.setQueryData<Awaited<ReturnType<typeof memberService.home>>>(
				[QUERY_KEYS.GET_MEMBER_HOME],
				(oldData) =>
					!oldData
						? oldData
						: {
								...oldData,
								active_cases: oldData.active_cases - 1,
							},
			);
		},
		onError: (_err, variables, context) => {
			if (!context) return;
			const previousCase = context.previousCase as Case;
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variables],
				context.previousCase,
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "active" }],
				(oldData) => infiniteDataAdd(oldData, "cases", previousCase, true),
			);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES, { status: "closed" }],
				(oldData) => infiniteDataRemove(oldData, "cases", "id", variables),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_ADD_MEMBER], {
		retry: 2,
		mutationFn: (payload: AddRemoveMemberCasePayloadSchema) =>
			caseService.addMember(payload),
		onSuccess: (_res, variables) => {
			const caseId = variables.caseId;
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_REMOVE_MEMBER], {
		retry: 2,
		mutationFn: (payload: AddRemoveMemberCasePayloadSchema) =>
			caseService.removeMember(payload),
		onSuccess: (_res, variables) => {
			const caseId = variables.caseId;
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_ASSIGN_TEAM], {
		retry: 2,
		mutationFn: (payload: AssignUnassignTeamPayload) =>
			caseService.assignCaseToTeam(payload),
		onSuccess: (_res, variables) => {
			const caseId = variables.caseId;
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_UNASSIGN_TEAM], {
		retry: 2,
		mutationFn: (payload: AssignUnassignTeamPayload) =>
			caseService.unassignCaseFromTeam(payload),
		onSuccess: (_res, variables) => {
			const caseId = variables.caseId;
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_REQUEST_ACCESS], {
		retry: 2,
		mutationFn: (payload: PostRequestAccessCasePayload) =>
			caseService.requestAccess(payload),
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_SEND_EMAIL], {
		retry: 2,
		mutationFn: (params: Parameters<typeof caseService.sendEmail>[0]) =>
			caseService.sendEmail(params),
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CASE_ENCOUNTER_CREATE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof encounterService.createCase>[0]) =>
			encounterService.createCase(payload),
	});
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_CREATE_CASE_MEDICAL_NOTES],
		{
			retry: 2,
			mutationFn: (
				payload: Parameters<typeof caseService.createMedicalNotes>[0],
			) => caseService.createMedicalNotes(payload),
			onMutate: async (payload) => {
				await queryClient.cancelQueries({
					queryKey: [QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.id],
				});
				const currentUser = queryClient.getQueryData<MemberProfileResponse>([
					QUERY_KEYS.GET_MEMBER_PROFILE,
				]);
				const created_at = getIsoDateTime();
				const optimisticMedicalNote: CaseMedicalNotes = {
					id: -1,
					text: payload.text,
					status: payload.status ?? "final",
					wound_analysis_id: payload.wound_analysis_id,
					user: currentUser?.user as CaseMedicalNotes["user"],
					created_at,
					updated_at: created_at,
				};

				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.id],
					(oldData) =>
						infiniteDataAdd(
							oldData,
							"case_medical_notes",
							optimisticMedicalNote,
						),
				);

				return { optimisticMedicalNote };
			},
			onSuccess: (res, payload, ctx) => {
				if (!ctx) return;
				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.id],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"case_medical_notes",
							"id",
							ctx.optimisticMedicalNote.id,
							res.case_medical_note,
						),
				);
			},
			onError: (_err, payload, ctx) => {
				if (!ctx) return;
				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.id],
					(oldData) =>
						infiniteDataRemove(
							oldData,
							"case_medical_notes",
							"id",
							ctx.optimisticMedicalNote.id,
						),
				);
			},
		},
	);
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_UPDATE_CASE_MEDICAL_NOTES],
		{
			retry: 2,
			mutationFn: (
				payload: Parameters<typeof caseService.updateMedicalNotes>[0],
			) => caseService.updateMedicalNotes(payload),
			onMutate: async (payload) => {
				await queryClient.cancelQueries({
					queryKey: [QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.caseId],
				});
				const currentUser = queryClient.getQueryData<MemberProfileResponse>([
					QUERY_KEYS.GET_MEMBER_PROFILE,
				]);
				const created_at = getIsoDateTime();
				const optimisticMedicalNote: CaseMedicalNotes = {
					id: payload.id,
					text: payload.text,
					user: currentUser?.user as CaseMedicalNotes["user"],
					created_at,
					updated_at: created_at,
				};

				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.caseId],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"case_medical_notes",
							"id",
							optimisticMedicalNote.id,
							optimisticMedicalNote,
						),
				);

				return { optimisticMedicalNote };
			},
			onSuccess: (res, payload, ctx) => {
				if (!ctx) return;
				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.caseId],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"case_medical_notes",
							"id",
							ctx.optimisticMedicalNote.id,
							res.case_medical_note,
						),
				);
			},
			onError: (_err, payload, ctx) => {
				if (!ctx) return;
				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload.caseId],
					(oldData) =>
						infiniteDataRemove(
							oldData,
							"case_medical_notes",
							"id",
							ctx.optimisticMedicalNote.id,
						),
				);
			},
		},
	);
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_DELETE_CASE_MEDICAL_NOTES],
		{
			retry: 2,
			mutationFn: (
				payload: Parameters<typeof caseService.removeMedicalNotes>[0],
			) => caseService.removeMedicalNotes(payload),
			onMutate: async (payload) => {
				await queryClient.cancelQueries({
					queryKey: [QUERY_KEYS.GET_CASE_MEDICAL_NOTES, payload],
				});
			},
		},
	);
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_SIGN_CASE_MEDICAL_NOTE], {
		mutationFn: (payload: {
			caseId: number | string;
			id: number | string;
		}) => caseService.signMedicalNote(payload.caseId, payload.id),
		onMutate: async (payload) => {
			const caseIdNum = Number(payload.caseId);
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE_MEDICAL_NOTES, caseIdNum],
			});
			const previousNotes = queryClient.getQueryData<
				InfiniteData<GetCaseMedicalNotesResponse>
			>([QUERY_KEYS.GET_CASE_MEDICAL_NOTES, caseIdNum]);

			queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
				[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, caseIdNum],
				(oldData) => {
					if (!oldData) return oldData;
					return {
						...oldData,
						pages: oldData.pages.map((page) => ({
							...page,
							case_medical_notes: page.case_medical_notes.map((note) =>
								note.id === Number(payload.id)
									? {
											...note,
											status: "final",
											signed_at: getIsoDateTime(),
										}
									: note,
							),
						})),
					};
				},
			);

			return { previousNotes, caseIdNum };
		},
		onSuccess: (res, payload, ctx) => {
			const updatedNote =
				res && typeof res === "object" && "case_medical_note" in res
					? res.case_medical_note
					: res;
			const caseIdNum = ctx?.caseIdNum ?? Number(payload.caseId);
			if (updatedNote) {
				queryClient.setQueryData<InfiniteData<GetCaseMedicalNotesResponse>>(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, caseIdNum],
					(oldData) => {
						if (!oldData) return oldData;
						return {
							...oldData,
							pages: oldData.pages.map((page) => ({
								...page,
								case_medical_notes: page.case_medical_notes.map((n) =>
									n.id === Number(payload.id)
										? (updatedNote as CaseMedicalNotes)
										: n,
								),
							})),
						};
					},
				);
			}
		},
		onError: (_err, _payload, ctx) => {
			if (ctx?.previousNotes && ctx?.caseIdNum) {
				queryClient.setQueryData(
					[QUERY_KEYS.GET_CASE_MEDICAL_NOTES, ctx.caseIdNum],
					ctx.previousNotes,
				);
			}
		},
		onSettled: (_res, _err, payload) => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CASE_MEDICAL_NOTES, Number(payload.caseId)],
			});
		},
	});
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_CASE_LETTERS_CREATE_NOTES],
		{
			retry: 2,
			mutationFn: (payload: Parameters<typeof caseService.createLetter>[0]) =>
				caseService.createLetter(payload),
			onSuccess: (res) => {
				queryClient.setQueryData<InfiniteData<GetCaseLettersResponse>>(
					[QUERY_KEYS.GET_CASE_LETTERS, res.case_letter.case_id],
					(oldData) =>
						infiniteDataAdd(oldData, "case_letters", res.case_letter, true),
				);
			},
		},
	);
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_CASE_LETTERS_UPDATE_NOTES],
		{
			retry: 2,
			mutationFn: (payload: Parameters<typeof caseService.updateLetter>[0]) =>
				caseService.updateLetter(payload),
			onSuccess: (res) => {
				queryClient.setQueryData<InfiniteData<GetCaseLettersResponse>>(
					[QUERY_KEYS.GET_CASE_LETTERS, res.case_letter.case_id],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"case_letters",
							"id",
							res.case_letter.id,
							res.case_letter,
						),
				);
			},
		},
	);
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_CASE_LETTERS_EMAIL_NOTES],
		{
			retry: 2,
			mutationFn: (payload: Parameters<typeof caseService.sendLetter>[0]) =>
				caseService.sendLetter(payload),
		},
	);
	queryClient.setMutationDefaults(
		[QUERY_KEYS.MUTATE_CASE_LETTERS_DELETE_NOTES],
		{
			retry: 2,
			mutationFn: (payload: Parameters<typeof caseService.deleteLetter>[0]) =>
				caseService.deleteLetter(payload),
		},
	);
};
